// src/components/BotMessagesPanel.jsx
//
// 2026-07-17 — Unified bot-message tracking (owner ask). Admin-only page to
// browse EVERY message the bot has sent (both send paths), search/filter by
// chat + type, and edit or delete a message straight from the CRM — the owner
// isn't a member of the target chats, so this is the only place to manage them.
//
// Backend: routes/bot-messages.cjs (requireAdmin). Reads via apiGet; edit/delete
// via direct authed POST (mirrors the admin direct-fetch idiom — apiSave is the
// debounced writer for the big shared tables and is the wrong tool here).
//
// Design: scoped `.bm-*` premium chrome (single-layer / borderless / emerald),
// own light|dark toggle. Themed through `.bm-root[data-theme]`, never through the
// global `.dark-mode` class — that class DOES reach this page (#blitz-theme-root
// wraps every screen, admin ones included; the older claim here that admin pages
// render outside it was wrong), it simply matches no `.bm-*` rule. Initial theme
// follows the global blitz_dark preference, then persists locally under
// botmessages_theme.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiGet } from '../lib/api-client.js';
import { API_BASE } from '../lib/endpoints.js';
import { authHeaders } from '../lib/auth-headers.js';
import { useToast } from '../contexts/toast-context.jsx';

const KIND_META = {
  finance: { label: 'Finance', color: '#0EA877' },
  brands:  { label: 'Brands',  color: '#0EA5E9' },
  monitor: { label: 'Monitor', color: '#8B5CF6' },
  leads:   { label: 'Leads',   color: '#F59E0B' },
  dm:      { label: 'DM',      color: '#64748B' },
  group:   { label: 'Group',   color: '#94A3B8' },
};
const TYPE_META = {
  payment_received: { label: 'Customer payment', color: '#0EA877' },
  aff_paid:         { label: 'Affiliate paid',   color: '#0EA5E9' },
  offer_reply:      { label: 'Offer reply',      color: '#F59E0B' },
  command_reply:    { label: 'Command',          color: '#64748B' },
  security_alert:   { label: 'Security',         color: '#E5484D' },
  '2fa':            { label: '2FA (masked)',     color: '#8B5CF6' },
  other:            { label: 'Other',            color: '#94A3B8' },
};
const PAGE_SIZE = 60;

function fmtTime(ts) {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('en-GB', {
      timeZone: 'Europe/Kyiv', day: '2-digit', month: 'short',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(ts); }
}

export function BotMessagesPanel({ user, onBack }) {
  const toast = useToast();
  const addToast = toast && toast.addToast ? toast.addToast : () => {};

  const [theme, setTheme] = useState(() => {
    const local = (typeof localStorage !== 'undefined') && localStorage.getItem('botmessages_theme');
    if (local === 'light' || local === 'dark') return local;
    const globalDark = (typeof localStorage !== 'undefined') && localStorage.getItem('blitz_dark');
    return globalDark === 'false' ? 'light' : 'dark';
  });
  const toggleTheme = () => setTheme(t => {
    const next = t === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem('botmessages_theme', next); } catch {}
    return next;
  });

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);

  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');      // chat_kind filter
  const [type, setType] = useState('');      // msg_type filter
  const [expanded, setExpanded] = useState(null); // `${chat_id}:${message_id}`

  const [editing, setEditing] = useState(null);   // { chat_id, message_id, text }
  const [editText, setEditText] = useState('');
  const [deleting, setDeleting] = useState(null); // row
  const [busy, setBusy] = useState(false);

  const qDebounce = useRef(null);

  const buildQuery = useCallback((off) => {
    const p = new URLSearchParams();
    if (q.trim()) p.set('q', q.trim());
    if (kind) p.set('kind', kind);
    if (type) p.set('type', type);
    p.set('limit', String(PAGE_SIZE));
    p.set('offset', String(off || 0));
    return p.toString();
  }, [q, kind, type]);

  const load = useCallback(async (off, append) => {
    setLoading(true); setError(null);
    try {
      const [list, st] = await Promise.all([
        apiGet(`admin/bot-messages?${buildQuery(off)}`),
        off ? Promise.resolve(null) : apiGet('admin/bot-messages/stats'),
      ]);
      if (list && list.ok) {
        setRows(prev => (append ? [...prev, ...(list.rows || [])] : (list.rows || [])));
        setTotal(list.total || 0);
        setOffset(off || 0);
      } else {
        setError((list && list.error) || 'Failed to load');
      }
      if (st && st.ok) setStats(st);
    } catch (e) {
      setError(e && e.message ? e.message : 'Network error');
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  // Reload when filters change (search debounced).
  useEffect(() => {
    if (qDebounce.current) clearTimeout(qDebounce.current);
    qDebounce.current = setTimeout(() => { load(0, false); }, 220);
    return () => { if (qDebounce.current) clearTimeout(qDebounce.current); };
  }, [q, kind, type, load]);

  const openEdit = (row) => { setEditing(row); setEditText(row.text || ''); };
  const closeEdit = () => { setEditing(null); setEditText(''); };

  const submitEdit = async () => {
    if (!editing || !editText.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(
        `${API_BASE}/admin/bot-messages/${encodeURIComponent(editing.chat_id)}/${encodeURIComponent(editing.message_id)}/edit`,
        { method: 'POST', headers: authHeaders(), body: JSON.stringify({ text: editText }) }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setRows(prev => prev.map(r =>
          (r.chat_id === editing.chat_id && r.message_id === editing.message_id)
            ? { ...r, text: editText, edited_at: Date.now() } : r));
        addToast('✅ Message edited', 'success');
        closeEdit();
      } else {
        addToast(`❌ Edit failed: ${data.detail || data.error || res.status}`, 'error');
      }
    } catch (e) {
      addToast(`❌ Edit failed: ${e && e.message}`, 'error');
    } finally { setBusy(false); }
  };

  const submitDelete = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      const res = await fetch(
        `${API_BASE}/admin/bot-messages/${encodeURIComponent(deleting.chat_id)}/${encodeURIComponent(deleting.message_id)}/delete`,
        { method: 'POST', headers: authHeaders() }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setRows(prev => prev.map(r =>
          (r.chat_id === deleting.chat_id && r.message_id === deleting.message_id)
            ? { ...r, deleted_at: Date.now() } : r));
        addToast('🗑️ Message deleted', 'success');
        setDeleting(null);
      } else {
        addToast(`❌ Delete failed: ${data.detail || data.error || res.status}`, 'error');
      }
    } catch (e) {
      addToast(`❌ Delete failed: ${e && e.message}`, 'error');
    } finally { setBusy(false); }
  };

  const kindChips = [{ k: '', label: 'All chats' }].concat(
    (stats?.byKind || []).map(x => ({ k: x.kind, label: `${(KIND_META[x.kind]?.label) || x.kind} ${x.n}` }))
  );
  const typeChips = [{ t: '', label: 'All types' }].concat(
    (stats?.byType || []).map(x => ({ t: x.type, label: `${(TYPE_META[x.type]?.label) || x.type} ${x.n}` }))
  );

  return (
    <div className="bm-root" data-theme={theme}>
      <style>{CSS}</style>
      <div className="bm-page">
        <div className="bm-win">
          {/* Header */}
          <div className="bm-hd">
            <button className="bm-back" onClick={onBack} title="Back to Admin">←</button>
            <div className="bm-hd-txt">
              <div className="bm-ttl">
                <span className="bm-dot" />🤖 Bot Messages
              </div>
              <div className="bm-sub">Every message the bot sends — search, edit, delete. {total} tracked{stats ? ` · ${stats.edited} edited · ${stats.deleted} deleted` : ''}</div>
            </div>
            <button className="bm-themebtn" onClick={toggleTheme} title="Toggle theme">
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
          </div>

          {/* KPI tiles */}
          <div className="bm-kpi">
            <div className="bm-tile">
              <div className="bm-tile-n mono">{stats ? stats.total : '—'}</div>
              <div className="bm-tile-l">Tracked (live)</div>
            </div>
            <div className="bm-tile">
              <div className="bm-tile-n mono">{stats ? stats.edited : '—'}</div>
              <div className="bm-tile-l">Edited</div>
            </div>
            <div className="bm-tile">
              <div className="bm-tile-n mono">{stats ? stats.deleted : '—'}</div>
              <div className="bm-tile-l">Deleted</div>
            </div>
            <div className="bm-tile">
              <div className="bm-tile-n mono">{(stats?.byKind || []).length}</div>
              <div className="bm-tile-l">Chats</div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="bm-tools">
            <input
              className="bm-search"
              placeholder="Search message text…"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
            <div className="bm-chips">
              {kindChips.map(c => (
                <button key={'k' + c.k} className={'bm-chip' + (kind === c.k ? ' on' : '')} onClick={() => setKind(c.k)}>{c.label}</button>
              ))}
            </div>
            <div className="bm-chips">
              {typeChips.map(c => (
                <button key={'t' + c.t} className={'bm-chip' + (type === c.t ? ' on' : '')} onClick={() => setType(c.t)}>{c.label}</button>
              ))}
            </div>
          </div>

          {/* Body */}
          {error && <div className="bm-state bm-err">⚠️ {error} <button className="bm-retry" onClick={() => load(0, false)}>Retry</button></div>}
          {!error && loading && rows.length === 0 && <div className="bm-state">Loading…</div>}
          {!error && !loading && rows.length === 0 && <div className="bm-state">No messages match this filter yet.</div>}

          {rows.length > 0 && (
            <div className="bm-table">
              <div className="bm-thead">
                <div>Time</div><div>Chat</div><div>Type</div><div>Message</div><div>Status</div><div className="bm-r">Actions</div>
              </div>
              <div className="bm-body">
                {rows.map(r => {
                  const id = `${r.chat_id}:${r.message_id}`;
                  const km = KIND_META[r.chat_kind] || { label: r.chat_kind || '—', color: '#94A3B8' };
                  const tm = TYPE_META[r.msg_type] || { label: r.msg_type || '—', color: '#94A3B8' };
                  const isOpen = expanded === id;
                  const gone = !!r.deleted_at;
                  return (
                    <div key={id} className={'bm-trow' + (gone ? ' gone' : '')}>
                      <div className="bm-time mono">{fmtTime(r.ts)}</div>
                      <div><span className="bm-badge" style={{ '--c': km.color }}>{km.label}</span></div>
                      <div><span className="bm-badge soft" style={{ '--c': tm.color }}>{tm.label}</span></div>
                      <div className="bm-msg" onClick={() => setExpanded(isOpen ? null : id)}>
                        <div className={isOpen ? 'bm-msg-full' : 'bm-msg-clip'}>{r.text || <span className="bm-muted">(empty)</span>}</div>
                        {r.business_key && <div className="bm-key mono">{r.business_key}</div>}
                      </div>
                      <div className="bm-flags">
                        {gone && <span className="bm-flag del">deleted</span>}
                        {!gone && r.edited_at && <span className="bm-flag ed">edited</span>}
                        {r.redacted ? <span className="bm-flag mask">masked</span> : null}
                        {!gone && !r.edited_at && !r.redacted && <span className="bm-muted">—</span>}
                      </div>
                      <div className="bm-actions bm-r">
                        {!gone && <button className="bm-act" onClick={() => openEdit(r)}>Edit</button>}
                        {!gone && <button className="bm-act danger" onClick={() => setDeleting(r)}>Delete</button>}
                        {gone && <span className="bm-muted">—</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Load more */}
          {rows.length > 0 && rows.length < total && (
            <div className="bm-more">
              <button className="bm-loadmore" disabled={loading} onClick={() => load(offset + PAGE_SIZE, true)}>
                {loading ? 'Loading…' : `Load more (${rows.length} / ${total})`}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Edit modal */}
      {editing && (
        <div className="bm-overlay" onClick={busy ? undefined : closeEdit}>
          <div className="bm-modal" onClick={e => e.stopPropagation()}>
            <div className="bm-modal-t">Edit message</div>
            <div className="bm-modal-meta mono">{(KIND_META[editing.chat_kind]?.label) || editing.chat_kind} · id {editing.message_id}</div>
            <textarea className="bm-textarea" value={editText} onChange={e => setEditText(e.target.value)} rows={7} autoFocus />
            <div className="bm-modal-hint">The bot will replace the live message in Telegram with this text.</div>
            <div className="bm-modal-row">
              <button className="bm-btn ghost" onClick={closeEdit} disabled={busy}>Cancel</button>
              <button className="bm-btn primary" onClick={submitEdit} disabled={busy || !editText.trim()}>{busy ? 'Saving…' : 'Save & edit in Telegram'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleting && (
        <div className="bm-overlay" onClick={busy ? undefined : () => setDeleting(null)}>
          <div className="bm-modal" onClick={e => e.stopPropagation()}>
            <div className="bm-modal-t">Delete this message?</div>
            <div className="bm-modal-prev">{(deleting.text || '').slice(0, 220) || '(empty)'}</div>
            <div className="bm-modal-hint">The bot will delete it from Telegram. Telegram only allows deleting recent messages — older ones may be refused.</div>
            <div className="bm-modal-row">
              <button className="bm-btn ghost" onClick={() => setDeleting(null)} disabled={busy}>Cancel</button>
              <button className="bm-btn danger" onClick={submitDelete} disabled={busy}>{busy ? 'Deleting…' : 'Delete from Telegram'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BotMessagesPanel;

const CSS = `
.bm-root{
  --bm-surface:#FFFFFF; --bm-soft:#EEF1F6; --bm-card:#FFFFFF;
  --bm-text:#14171D; --bm-dim:#6F7884; --bm-muted:#9AA1AC; --bm-line:#E7EBF0;
  --bm-em:#0EA877; --bm-em-soft:#E5F7EF; --bm-em-text:#097A53;
  --bm-danger:#E5484D; --bm-danger-soft:#FDECEC;
  --bm-sh-win:0 1px 3px rgba(18,28,42,.04),0 28px 64px -36px rgba(18,28,42,.24);
  --bm-sh-card:0 1px 2px rgba(18,28,42,.05),0 14px 32px -18px rgba(18,28,42,.16);
  --bm-ease:cubic-bezier(.32,.72,0,1);
  min-height:100vh; background:var(--bm-soft); color:var(--bm-text);
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
}
.bm-root[data-theme=dark]{
  --bm-surface:#11151C; --bm-soft:#0C1016; --bm-card:#181D26;
  --bm-text:#E8ECF2; --bm-dim:#95A0AD; --bm-muted:#68727F; --bm-line:#232A34;
  --bm-em:#2DD4A7; --bm-em-soft:#0E2A22; --bm-em-text:#57E0B6;
  --bm-danger:#FF6369; --bm-danger-soft:#2A1518;
  --bm-sh-win:0 1px 3px rgba(0,0,0,.3),0 28px 64px -36px rgba(0,0,0,.7);
  --bm-sh-card:0 1px 2px rgba(0,0,0,.3),0 14px 32px -18px rgba(0,0,0,.55);
}
.bm-root .mono{ font-family:'JetBrains Mono',ui-monospace,SFMono-Regular,Menlo,monospace; font-variant-numeric:tabular-nums; }
.bm-page{ padding:24px; display:flex; justify-content:center; }
.bm-win{ width:100%; max-width:1180px; background:var(--bm-surface); border-radius:20px; padding:28px 30px 30px; box-shadow:var(--bm-sh-win); }
.bm-hd{ display:flex; align-items:center; gap:16px; margin-bottom:22px; }
.bm-back,.bm-themebtn{ width:38px; height:38px; border-radius:11px; border:none; background:var(--bm-soft); color:var(--bm-text); cursor:pointer; font-size:16px; transition:.18s var(--bm-ease); flex:0 0 auto; }
.bm-back:hover,.bm-themebtn:hover{ background:var(--bm-card); box-shadow:var(--bm-sh-card); }
.bm-hd-txt{ flex:1 1 auto; min-width:0; }
.bm-ttl{ display:flex; align-items:center; gap:10px; font-size:21px; font-weight:800; letter-spacing:-.4px; }
.bm-sub{ color:var(--bm-dim); font-size:13px; margin-top:3px; }
.bm-dot{ width:9px; height:9px; border-radius:50%; background:var(--bm-em); box-shadow:0 0 0 0 var(--bm-em); animation:bm-pulse 2.4s infinite; }
@keyframes bm-pulse{ 0%{ box-shadow:0 0 0 0 rgba(14,168,119,.5);} 70%{ box-shadow:0 0 0 8px rgba(14,168,119,0);} 100%{ box-shadow:0 0 0 0 rgba(14,168,119,0);} }
.bm-kpi{ display:grid; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); gap:13px; margin-bottom:20px; }
.bm-tile{ background:var(--bm-card); border-radius:15px; padding:16px 18px; box-shadow:var(--bm-sh-card); }
.bm-tile-n{ font-size:26px; font-weight:800; letter-spacing:-.5px; }
.bm-tile-l{ color:var(--bm-dim); font-size:12px; margin-top:2px; }
.bm-tools{ display:flex; flex-direction:column; gap:11px; margin-bottom:18px; }
.bm-search{ width:100%; box-sizing:border-box; padding:12px 15px; border-radius:12px; border:none; background:var(--bm-soft); color:var(--bm-text); font-size:14px; outline:none; box-shadow:inset 0 0 0 1.5px transparent; transition:.18s; }
.bm-search:focus{ box-shadow:inset 0 0 0 1.5px var(--bm-em); }
.bm-chips{ display:flex; flex-wrap:wrap; gap:7px; }
.bm-chip{ padding:6px 12px; border-radius:9px; border:none; background:var(--bm-soft); color:var(--bm-dim); font-size:12px; font-weight:600; cursor:pointer; transition:.15s var(--bm-ease); }
.bm-chip:hover{ color:var(--bm-text); }
.bm-chip.on{ background:var(--bm-em-soft); color:var(--bm-em-text); box-shadow:inset 0 0 0 1.5px var(--bm-em); }
.bm-table{ display:flex; flex-direction:column; }
.bm-thead,.bm-trow{ display:grid; grid-template-columns:118px 96px 138px 1fr 96px 138px; gap:12px; align-items:center; }
.bm-thead{ padding:0 14px 10px; color:var(--bm-muted); font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.5px; }
.bm-body{ display:flex; flex-direction:column; gap:4px; }
.bm-trow{ padding:12px 14px; border-radius:12px; transition:.15s var(--bm-ease); }
.bm-trow:hover{ background:var(--bm-card); box-shadow:var(--bm-sh-card); }
.bm-trow.gone{ opacity:.5; }
.bm-r{ justify-self:end; text-align:right; }
.bm-time{ font-size:12px; color:var(--bm-dim); }
.bm-badge{ display:inline-block; padding:3px 9px; border-radius:7px; font-size:11px; font-weight:700; color:var(--c); background:color-mix(in srgb,var(--c) 14%,transparent); }
.bm-badge.soft{ background:color-mix(in srgb,var(--c) 10%,transparent); }
.bm-msg{ cursor:pointer; min-width:0; }
.bm-msg-clip{ white-space:nowrap; overflow:hidden; text-overflow:ellipsis; font-size:13px; }
.bm-msg-full{ white-space:pre-wrap; word-break:break-word; font-size:13px; }
.bm-key{ font-size:11px; color:var(--bm-muted); margin-top:3px; }
.bm-muted{ color:var(--bm-muted); }
.bm-flags{ display:flex; flex-wrap:wrap; gap:4px; }
.bm-flag{ font-size:10px; font-weight:700; padding:2px 7px; border-radius:6px; text-transform:uppercase; letter-spacing:.3px; }
.bm-flag.ed{ background:var(--bm-em-soft); color:var(--bm-em-text); }
.bm-flag.del{ background:var(--bm-danger-soft); color:var(--bm-danger); }
.bm-flag.mask{ background:rgba(139,92,246,.14); color:#8B5CF6; }
.bm-actions{ display:flex; gap:6px; }
.bm-act{ padding:6px 12px; border-radius:9px; border:none; background:var(--bm-soft); color:var(--bm-text); font-size:12px; font-weight:600; cursor:pointer; transition:.15s var(--bm-ease); }
.bm-act:hover{ background:var(--bm-em-soft); color:var(--bm-em-text); }
.bm-act.danger:hover{ background:var(--bm-danger-soft); color:var(--bm-danger); }
.bm-state{ padding:40px; text-align:center; color:var(--bm-dim); font-size:14px; }
.bm-err{ color:var(--bm-danger); }
.bm-retry,.bm-loadmore{ margin-left:10px; padding:7px 14px; border-radius:9px; border:none; background:var(--bm-em); color:#fff; font-size:13px; font-weight:600; cursor:pointer; }
.bm-more{ display:flex; justify-content:center; margin-top:16px; }
.bm-loadmore{ margin:0; background:var(--bm-soft); color:var(--bm-text); }
.bm-loadmore:hover{ box-shadow:var(--bm-sh-card); }
.bm-overlay{ position:fixed; inset:0; background:rgba(8,12,20,.55); backdrop-filter:blur(3px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
.bm-modal{ width:100%; max-width:540px; background:var(--bm-surface); border-radius:18px; padding:24px; box-shadow:var(--bm-sh-win); }
.bm-modal-t{ font-size:18px; font-weight:800; letter-spacing:-.3px; }
.bm-modal-meta{ color:var(--bm-muted); font-size:12px; margin:4px 0 14px; }
.bm-textarea{ width:100%; box-sizing:border-box; padding:13px 15px; border-radius:12px; border:none; background:var(--bm-soft); color:var(--bm-text); font-size:13px; font-family:inherit; line-height:1.5; resize:vertical; outline:none; box-shadow:inset 0 0 0 1.5px transparent; }
.bm-textarea:focus{ box-shadow:inset 0 0 0 1.5px var(--bm-em); }
.bm-modal-prev{ background:var(--bm-soft); border-radius:12px; padding:13px 15px; font-size:13px; white-space:pre-wrap; word-break:break-word; max-height:200px; overflow:auto; }
.bm-modal-hint{ color:var(--bm-dim); font-size:12px; margin:11px 0 16px; }
.bm-modal-row{ display:flex; justify-content:flex-end; gap:10px; }
.bm-btn{ padding:10px 18px; border-radius:11px; border:none; font-size:13px; font-weight:700; cursor:pointer; transition:.15s var(--bm-ease); }
.bm-btn:disabled{ opacity:.5; cursor:default; }
.bm-btn.ghost{ background:var(--bm-soft); color:var(--bm-text); }
.bm-btn.primary{ background:var(--bm-em); color:#fff; }
.bm-btn.danger{ background:var(--bm-danger); color:#fff; }
@media(max-width:820px){
  .bm-thead{ display:none; }
  .bm-trow{ grid-template-columns:1fr 1fr; gap:8px; padding:14px; background:var(--bm-card); box-shadow:var(--bm-sh-card); }
  .bm-msg{ grid-column:1 / -1; order:5; }
  .bm-actions{ grid-column:1 / -1; order:6; justify-self:start; }
  .bm-r{ justify-self:start; text-align:left; }
}
`;
